// Coming back from Surepass: the portal lands on /Home/Dashboard?allotmentEsign=<allotmentEsignId>.
// handleEsignReturn confirms the signature with the server, tells the customer, and removes the param.
import { completeEsign } from "../../Services/AllotmentEsign";
import { errorTost, infoTost, successTost } from "../../Services/Toast";
import { dismissLoader, presentLoader } from "../../Services/loader";

export const ESIGN_PARAM = "allotmentEsign";
const NOT_COMPLETED = "eSign was not completed. Please try again.";
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
export const LOAD_ERROR = "Unable to load the terms. Please try again.";

// A 401 from /pending carries "Please log in again": show it. Everything else keeps the load-error copy.
export const pendingErrorText = (err) => ([401, 409].includes(err?.status) && err?.message ? err.message : LOAD_ERROR);

export const NAME_MISMATCH_GENERIC =
	"The name on your Aadhaar does not match your KYC name. Please eSign again with your own Aadhaar.";

// ({ status, failReason, remaining, signerName, aadhaarName }) → { kind: "success" | "info" | "error", text, nameMismatch? }
// signerName is the KYC name, aadhaarName the name on the Aadhaar eSign certificate.
export function returnMessage({ status, failReason, remaining, signerName, aadhaarName } = {}) {
	if (status === "COMPLETED") {
		const left = Number(remaining) || 0;
		return left > 0
			? { kind: "info", text: `Terms signed. ${left} more signature(s) needed before payment.` }
			: { kind: "success", text: "Terms signed. You can now pay." };
	}
	if (status === "PENDING") return { kind: "info", text: "We're confirming your eSign. Please try Pay Now in a minute." };
	if (status === "FAILED" && failReason === "NAME_MISMATCH") {
		return {
			kind: "error",
			nameMismatch: true,
			text:
				aadhaarName && signerName
					? `The name on your Aadhaar (${aadhaarName}) does not match your KYC name (${signerName}). Please eSign again with your own Aadhaar.`
					: NAME_MISMATCH_GENERIC,
		};
	}
	return { kind: "error", text: NOT_COMPLETED };
}

const TOAST = { success: successTost, info: infoTost, error: errorTost };
const handled = new Set(); // one /complete per id, even if the effect runs twice (StrictMode, remount)

// onNameMismatch({ text, allotmentPaymentId }): optional; when given, a name mismatch goes there (to offer
// Retry eSign) instead of the error toast.
export async function handleEsignReturn(search, navigate, pathname = window.location.pathname, { onNameMismatch } = {}) {
	const params = new URLSearchParams(search || "");
	const raw = params.get(ESIGN_PARAM);
	if (raw == null) return;
	// Surepass may append "?status=..." to our callback, so the value can be "<uuid>?status=success"
	const id = (raw.match(UUID_RE) || [])[0];
	if (!id) {
		params.delete(ESIGN_PARAM);
		const left = params.toString();
		navigate(left ? `${pathname}?${left}` : pathname, { replace: true });
		return;
	}
	if (handled.has(id)) return;
	handled.add(id);

	presentLoader();
	try {
		const result = await completeEsign(id);
		const { kind, text, nameMismatch } = returnMessage(result || {});
		if (nameMismatch && onNameMismatch && result?.allotmentPaymentId) {
			onNameMismatch({ text, allotmentPaymentId: result.allotmentPaymentId });
		} else {
			TOAST[kind](text);
		}
	} catch (err) {
		errorTost(err?.message || NOT_COMPLETED);
	} finally {
		dismissLoader();
		params.delete(ESIGN_PARAM);
		const rest = params.toString();
		navigate(rest ? `${pathname}?${rest}` : pathname, { replace: true });
	}
}
